"use client";

import { useState } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { supabase } from "@/utils/supabase/client";
import { useQueryClient } from "@tanstack/react-query";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { showSuccess, showError } from "@/utils/toast";

export const InviteClientForm = ({ onSuccess }: { onSuccess?: () => void }) => {
  const { profile, company } = useAuth();
  const queryClient = useQueryClient();
  const [email, setEmail] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!company?.id) return;
    setIsSubmitting(true);
    try {
      // Create an invite for a client (no KYC)
      const { error } = await supabase.from("invites").insert({
        email,
        company_id: company.id,
        role: "client",
      });
      if (error) throw error;
      showSuccess(`Invite sent to ${email}!`);
      setEmail("");
      queryClient.invalidateQueries({ queryKey: ["invites"] });
      onSuccess?.();
    } catch (err: any) {
      showError(err.message || "Failed to send invite");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle>Invite Client</CardTitle>
      </CardHeader>
      <CardContent>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <Label htmlFor="invite-email">Client Email *</Label>
            <Input
              id="invite-email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
            />
          </div>
          <Button type="submit" disabled={isSubmitting} className="w-full">
            {isSubmitting ? "Sending..." : "Send Invite"}
          </Button>
        </form>
      </CardContent>
    </Card>
  );
};